import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enAbout from "./locales/en/about.json";
import enBanner from "./locales/en/banner.json";
import enBrand from "./locales/en/brand.json";
import enCommon from "./locales/en/common.json";
import enFeedback from "./locales/en/feedback.json";
import enIedPanel from "./locales/en/iedPanel.json";
import enLanding from "./locales/en/landing.json";
import enSignals from "./locales/en/signals.json";
import enWorkspace from "./locales/en/workspace.json";
import esAbout from "./locales/es/about.json";
import esBanner from "./locales/es/banner.json";
import esBrand from "./locales/es/brand.json";
import esCommon from "./locales/es/common.json";
import esFeedback from "./locales/es/feedback.json";
import esIedPanel from "./locales/es/iedPanel.json";
import esLanding from "./locales/es/landing.json";
import esSignals from "./locales/es/signals.json";
import esWorkspace from "./locales/es/workspace.json";

/** Clave de localStorage con la elección de idioma del operador. Se
 *  mantiene bajo el espacio `axon.*` para que todo lo que la app
 *  persiste en el navegador sea fácil de inventariar. */
export const I18N_STORAGE_KEY = "axon.tags.i18n.lang";

/** Idiomas soportados por la interfaz. La app cae a `es` para
 *  cualquier locale del navegador fuera de este conjunto. Añadir un
 *  idioma es un cambio solo de datos: agrega los JSON en
 *  `locales/<code>/`, registra el código aquí y el selector del diálogo
 *  About lo recoge automáticamente. */
export const SUPPORTED_LANGUAGES = ["es", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: {
        common: esCommon,
        landing: esLanding,
        workspace: esWorkspace,
        about: esAbout,
        feedback: esFeedback,
        banner: esBanner,
        brand: esBrand,
        iedPanel: esIedPanel,
        signals: esSignals,
      },
      en: {
        common: enCommon,
        landing: enLanding,
        workspace: enWorkspace,
        about: enAbout,
        feedback: enFeedback,
        banner: enBanner,
        brand: enBrand,
        iedPanel: enIedPanel,
        signals: enSignals,
      },
    },
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: "es",
    defaultNS: "workspace",
    ns: ["common", "landing", "workspace", "about", "feedback", "banner", "brand", "iedPanel", "signals"],
    interpolation: {
      // React ya escapa por defecto, así que el escape de i18next solo
      // añade sobrecarga.
      escapeValue: false,
      // Habilita `{{count, number}}` en las plantillas — delega en
      // `Intl.NumberFormat(currentLanguage)` para que el separador de
      // miles coincida con el locale del operador.
      format: (value, format, lng) => {
        if (format === "number" && typeof value === "number") {
          return new Intl.NumberFormat(lng ?? "es").format(value);
        }
        return String(value);
      },
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: I18N_STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnNull: false,
    // Los recursos se empaquetan de forma síncrona, así que no hay nada
    // que esperar. Omitir Suspense mantiene simple el árbol raíz.
    react: { useSuspense: false },
  });

// Mantener el atributo `lang` del documento en sincronía con el idioma
// activo, para lectores de pantalla y reglas CSS por idioma.
const applyDocumentLang = (lng: string) => {
  document.documentElement.lang = lng;
};
applyDocumentLang(i18n.resolvedLanguage ?? "es");
i18n.on("languageChanged", applyDocumentLang);

export default i18n;
